import React, { useState, useCallback, useRef } from 'react';
import { Box, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';

const ImageWrapper = styled(Box)({
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  height: '100%',
});

const OptimizedImage = ({
  src,
  alt,
  fallbackSrc,
  priority = false,
  className,
  sx,
  onError: externalOnError,
  wrapperSx,
  skeletonVariant = 'rectangular',
}) => {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  const revealImage = useCallback(() => {
    const img = imgRef.current;
    if (!img) {
      setLoaded(true);
      return;
    }
    if (typeof img.decode === 'function') {
      img.decode().then(() => setLoaded(true)).catch(() => setLoaded(true));
    } else {
      setLoaded(true);
    }
  }, []);

  const handleError = useCallback(
    (e) => {
      if (fallbackSrc && e.target.src !== fallbackSrc) {
        e.target.src = fallbackSrc;
        return;
      }
      setLoaded(true);
      if (externalOnError) {
        externalOnError(e);
      }
    },
    [fallbackSrc, externalOnError]
  );

  return (
    <ImageWrapper sx={wrapperSx}>
      {!loaded && (
        <Skeleton
          variant={skeletonVariant}
          animation="wave"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: 'inherit',
          }}
        />
      )}
      <Box
        component="img"
        ref={imgRef}
        src={src}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        className={className}
        onLoad={revealImage}
        onError={handleError}
        sx={[
          {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            opacity: loaded ? 1 : 0,
            transition: loaded
              ? 'opacity 0.3s ease-in-out, transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
              : 'none',
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      />
    </ImageWrapper>
  );
};

export default OptimizedImage;
